import { useState } from 'react'
import {
	Box,
	Typography,
	TextField,
	Stack,
	Collapse
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import StoreHoursEditor from './StoreHoursEditor'

const cleanPhoneNumber = (phone) => {
	if (!phone) return ''
	return phone.replace(/\D/g, '').slice(0, 10)
}

const formatPhoneWhileTyping = (digits) => {
	if (!digits) return ''
	const cleaned = digits.replace(/\D/g, '').slice(0, 10)
	if (cleaned.length === 0) return ''
	if (cleaned.length <= 3) return `(${cleaned}`
	if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
	return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
}

// Collapsible wrapper (same as in LandingPageSettings)
function CollapsibleSection({ title, defaultExpanded = false, children, isMobile }) {
	const [expanded, setExpanded] = useState(defaultExpanded)

	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				border: '1px solid rgba(0, 0, 0, 0.2)'
			}}
		>
			<Box
				onClick={() => setExpanded(!expanded)}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: '20px 24px',
					cursor: 'pointer',
					userSelect: 'none',
					borderRadius: expanded ? '12px 12px 0 0' : '12px',
					'&:hover': { bgcolor: '#FAFAFA' }
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 400,
						fontSize: isMobile ? '18px' : '22px',
						lineHeight: '1.5em',
						color: '#000000',
						flex: 1
					}}
				>
					{title}
				</Typography>
				{expanded ? (
					<ExpandLessIcon sx={{ color: '#4A5568' }} />
				) : (
					<ExpandMoreIcon sx={{ color: '#4A5568' }} />
				)}
			</Box>
			<Collapse in={expanded}>
				<Box sx={{ px: '24px', pb: '24px' }}>
					{children}
				</Box>
			</Collapse>
		</Box>
	)
}

// Reusable labeled input field matching Figma style
function LabeledField({ label, value, onChange, placeholder, isMobile, ...props }) {
	return (
		<Box>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '12px', letterSpacing: '-0.021em', color: '#2D3748', mb: '4px' }}>
				{label}
			</Typography>
			<TextField
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				fullWidth
				size="small"
				InputProps={{
					sx: {
						bgcolor: '#FFFFFF',
						borderRadius: '6px',
						fontFamily: 'Inter, sans-serif',
						fontSize: isMobile ? '13px' : '14px',
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: 'rgba(0, 0, 0, 0.2)'
						}
					},
					style: {
						fontSize: isMobile ? '13px' : '14px'
					}
				}}
				{...props}
			/>
		</Box>
	)
}

export default function BusinessInfoSection({
	bizDescription, setBizDescription,
	bizPhone, setBizPhone,
	bizEmail, setBizEmail,
	bizAddress, setBizAddress,
	bizAddress2, setBizAddress2,
	bizCity, setBizCity,
	bizState, setBizState,
	bizZip, setBizZip,
	bizWebsite, setBizWebsite,
	bizSocialLinks, setBizSocialLinks,
	bizInheritedHours, setBizInheritedHours,
	bizWeeklyHoursInherited,
	bizWeeklyHoursDisplay, setBizWeeklyHoursDisplay,
	bizClosedDates,
	isMobile = false
}) {
	const [activeTab, setActiveTab] = useState('about')
	const tabs = [
		{ id: 'about', label: isMobile ? 'About' : 'About Us' },
		{ id: 'contact', label: 'Contact' },
		{ id: 'hours', label: 'Hours' },
		{ id: 'address', label: 'Address' },
		{ id: 'socials', label: 'Socials' }
	]

	return (
		<CollapsibleSection title="Business Information" defaultExpanded={false} isMobile={isMobile}>
			{/* Tab Navigation */}
			<Box
				sx={{
					display: 'flex',
					borderBottom: '0.8px solid #CBD5E0',
					mb: isMobile ? '20px' : '30px',
					mx: isMobile ? '-14px' : 0
				}}
			>
				{tabs.map((tab) => (
					<Box
						key={tab.id}
						onClick={() => setActiveTab(tab.id)}
						sx={{
							flex: 1,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							py: '14px',
							cursor: 'pointer',
							borderBottom: activeTab === tab.id ? '3px solid #B521FF' : '3px solid transparent',
							transition: 'border-color 0.2s ease'
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 500,
								fontSize: isMobile ? '14px' : '16px',
								lineHeight: '1.21em',
								letterSpacing: '-0.016em',
								color: activeTab === tab.id ? '#B521FF' : 'rgba(0, 0, 0, 0.5)',
								textAlign: 'center'
							}}
						>
							{tab.label}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Tab Content */}
			{activeTab === 'about' && (
				<Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '4px' : '12px', mb: '10px' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '1.5em', color: '#000' }}>
							About Us
						</Typography>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '1.21em', letterSpacing: '-0.018em', color: '#A0AEC0' }}>
							Let your customers know what your store is all about.
						</Typography>
					</Box>
					<Box
						sx={{
							border: '1px solid rgba(0, 0, 0, 0.2)',
							borderRadius: '10px',
							p: '20px'
						}}
					>
						<TextField
							value={bizDescription}
							onChange={(e) => setBizDescription(e.target.value)}
							placeholder="Tell customers about your business..."
							fullWidth
							multiline
							rows={12}
							variant="standard"
							InputProps={{
								disableUnderline: true,
								sx: {
									fontFamily: 'Inter, sans-serif',
									fontWeight: 400,
									fontSize: '13px',
									lineHeight: '1.21em',
									letterSpacing: '-0.019em',
									color: 'rgba(0, 0, 0, 0.8)'
								},
								style: {
									fontSize: '14px'
								}
							}}
						/>
					</Box>
				</Box>
			)}

			{activeTab === 'contact' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '14px' : '18px', }}>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '1.5em', color: '#000' }}>
						Contact Information
					</Typography>
					<LabeledField
						label="Phone"
						isMobile={isMobile}
						value={formatPhoneWhileTyping(bizPhone)}
						onChange={(e) => setBizPhone(cleanPhoneNumber(e.target.value))}
						placeholder="(555) 123-4567"
						inputMode="tel"
					/>
					<LabeledField
						label="Email"
						isMobile={isMobile}
						value={bizEmail}
						onChange={(e) => setBizEmail(e.target.value)}
						placeholder="hello@yourstore.com"
						type="email"
					/>
				</Box>
			)}

			{activeTab === 'hours' && (
				<Box>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '1.5em', color: '#000', mb: '18px' }}>
						Business Hours
					</Typography>
					<StoreHoursEditor
						inherited={bizInheritedHours}
						onToggleInherited={(val) => setBizInheritedHours(val)}
						weeklyHoursInherited={bizWeeklyHoursInherited}
						weeklyHoursDisplay={bizWeeklyHoursDisplay}
						setWeeklyHoursDisplay={setBizWeeklyHoursDisplay}
						closedDates={bizClosedDates}
						isMobile={isMobile}
					/>
				</Box>
			)}

			{activeTab === 'address' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '14px' : '18px', }}>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '1.5em', color: '#000' }}>
						Business Address
					</Typography>
					<LabeledField
						label="Street address"
						isMobile={isMobile}
						value={bizAddress}
						onChange={(e) => setBizAddress(e.target.value)}
						placeholder="2500 Wilshire Blvd"
					/>
					<LabeledField
						label="Street address 2"
						isMobile={isMobile}
						value={bizAddress2}
						onChange={(e) => setBizAddress2(e.target.value)}
						placeholder="Unit 12"
					/>
					<Box sx={{ display: 'flex', gap: '18px' }}>
						<Box sx={{ flex: 1 }}>
							<LabeledField
								label="City"
								isMobile={isMobile}
								value={bizCity}
								onChange={(e) => setBizCity(e.target.value)}
								placeholder="Los Angeles"
							/>
						</Box>
						<Box sx={{ flex: 1 }}>
							<LabeledField
								label="State/Province"
								isMobile={isMobile}
								value={bizState}
								onChange={(e) => setBizState(e.target.value)}
								placeholder="CA"
							/>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', gap: '18px' }}>
						<Box sx={{ flex: 1 }}>
							<LabeledField
								label="ZIP/Postal code"
								isMobile={isMobile}
								value={bizZip}
								onChange={(e) => setBizZip(e.target.value)}
								placeholder="90057"
							/>
						</Box>
						<Box sx={{ flex: 1 }} />
					</Box>
				</Box>
			)}

			{activeTab === 'socials' && (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '14px' : '18px', }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '12px' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', lineHeight: '1.5em', color: '#000' }}>
							Connect socials
						</Typography>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '1.21em', letterSpacing: '-0.018em', color: '#A0AEC0' }}>
							Add your social media links to help your customers find you everywhere.
						</Typography>
					</Box>
					<LabeledField
						label="Website"
						isMobile={isMobile}
						value={bizWebsite}
						onChange={(e) => setBizWebsite(e.target.value)}
						placeholder="https://yourstore.com"
					/>
					<LabeledField
						label="Facebook"
						isMobile={isMobile}
						value={bizSocialLinks.facebook}
						onChange={(e) => setBizSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
						placeholder="https://facebook.com/yourstore"
					/>
					<LabeledField
						label="Instagram"
						isMobile={isMobile}
						value={bizSocialLinks.instagram}
						onChange={(e) => setBizSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
						placeholder="https://instagram.com/yourstore"
					/>
					<LabeledField
						label="Yelp"
						isMobile={isMobile}
						value={bizSocialLinks.yelp}
						onChange={(e) => setBizSocialLinks(prev => ({ ...prev, yelp: e.target.value }))}
						placeholder="https://yelp.com/biz/yourstore"
					/>
					<LabeledField
						label="Google Business"
						isMobile={isMobile}
						value={bizSocialLinks.google}
						onChange={(e) => setBizSocialLinks(prev => ({ ...prev, google: e.target.value }))}
						placeholder="https://g.page/yourstore"
					/>
				</Box>
			)}
		</CollapsibleSection>
	)
}
